import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import useHook from './useHook';
import styles from './styles.module.scss';

type OpcionRelacion = {
  id: number
  attributes?: { nombre?: string, slug?: string }
}

type OpcionColor = {
  id: number
  attributes?: { nombre?: string, hexadecimal?: string }
}

type FiltrosTiendaProps = {
  tipos?: OpcionRelacion[]
  tematicas?: OpcionRelacion[]
  colores?: OpcionColor[]
  tipo: string[]
  tematica: string[]
  color: string[]
  destacado: boolean
  novedad: boolean
  onChangeTipo: (v: string[]) => void
  onChangeTematica: (v: string[]) => void
  onChangeColor: (v: string[]) => void
  onToggleDestacado: () => void
  onToggleNovedad: () => void
  onClear: () => void
}

// el Menu de MUI se renderiza en un portal con z-index 1300 por defecto,
// que queda por detrás del drawer de filtros mobile (z-index 99999)
const menuProps = { style: { zIndex: 100000 } };

const FiltrosFields = ({
  tipos, tematicas, colores,
  tipo, tematica, color, destacado, novedad,
  onChangeTipo, onChangeTematica, onChangeColor, onToggleDestacado, onToggleNovedad, onClear,
  activeCount,
}: FiltrosTiendaProps & { activeCount: number }) => (
  <div className={styles.fields}>

    <div className={styles.fieldGroup}>
      <FormControl fullWidth size="small">
        <InputLabel id="tipo">Tipo de producto</InputLabel>
        <Select
          labelId="tipo"
          multiple
          value={tipo}
          label="Tipo de producto"
          onChange={(e) => onChangeTipo(
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[]
          )}
          renderValue={(selected) => {
            const slugs = selected as string[];
            if (!slugs.length) return 'Todos';
            return slugs
              .map(slug => tipos?.find(t => t.attributes?.slug === slug)?.attributes?.nombre || slug)
              .join(', ');
          }}
          color="secondary"
          size="small"
          MenuProps={menuProps}
        >
          {tipos?.map(item => (
            <MenuItem value={item.attributes?.slug} key={item.id}>
              <Checkbox checked={tipo.includes(item.attributes?.slug || '')} size="small" />
              {item.attributes?.nombre}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>

    <div className={styles.fieldGroup}>
      <FormControl fullWidth size="small">
        <InputLabel id="tematica">Temática</InputLabel>
        <Select
          labelId="tematica"
          multiple
          value={tematica}
          label="Temática"
          onChange={(e) => onChangeTematica(
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[]
          )}
          renderValue={(selected) => {
            const slugs = selected as string[];
            if (!slugs.length) return 'Todas';
            return slugs
              .map(slug => tematicas?.find(t => t.attributes?.slug === slug)?.attributes?.nombre || slug)
              .join(', ');
          }}
          color="secondary"
          size="small"
          MenuProps={menuProps}
        >
          {tematicas?.map(item => (
            <MenuItem value={item.attributes?.slug} key={item.id}>
              <Checkbox checked={tematica.includes(item.attributes?.slug || '')} size="small" />
              {item.attributes?.nombre}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>

    {colores && colores.length > 0 &&
      <div className={styles.fieldGroup}>
        <FormControl fullWidth size="small">
          <InputLabel id="color">Color</InputLabel>
          <Select
            labelId="color"
            multiple
            value={color}
            label="Color"
            onChange={(e) => onChangeColor(
              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[]
            )}
            color="secondary"
            size="small"
            MenuProps={menuProps}
            renderValue={(selected) => {
              const nombres = selected as string[];
              if (!nombres.length) return 'Todos';
              return (
                <span className={styles.colorOption}>
                  {nombres.map(nombre => {
                    const selectedColor = colores?.find(c => c.attributes?.nombre === nombre);
                    return (
                      <span
                        key={nombre}
                        className={styles.colorSwatch}
                        style={{ backgroundColor: selectedColor?.attributes?.hexadecimal }}
                      />
                    );
                  })}
                  {nombres.join(', ')}
                </span>
              );
            }}
          >
            {colores?.map(item => (
              <MenuItem value={item.attributes?.nombre} key={item.id}>
                <Checkbox checked={color.includes(item.attributes?.nombre || '')} size="small" />
                <span className={styles.colorOption}>
                  <span className={styles.colorSwatch} style={{ backgroundColor: item.attributes?.hexadecimal }} />
                  {item.attributes?.nombre}
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    }

    <div className={styles.fieldGroup}>
      <FormControlLabel
        control={<Checkbox checked={destacado} onChange={onToggleDestacado} color="secondary" size="small" />}
        label="Destacados"
      />
      <FormControlLabel
        control={<Checkbox checked={novedad} onChange={onToggleNovedad} color="secondary" size="small" />}
        label="Novedades"
      />
    </div>

    {activeCount > 0 &&
      <button type="button" className={styles.clearButton} onClick={onClear}>
        Limpiar filtros
      </button>
    }

  </div>
);

const FiltrosTienda = (props: FiltrosTiendaProps) => {

  const { drawerOpen, open, closeButtonRef, openDrawer, closeDrawer, collapsed, toggleCollapsed, stickyTop } = useHook();

  const activeCount = [
    props.tipo.length ? 1 : '', props.tematica.length ? 1 : '', props.color.length ? 1 : '',
    props.destacado ? 1 : '', props.novedad ? 1 : '',
  ].filter(Boolean).length;

  // en mobile, aplicar un filtro cierra el drawer solo (no hace falta tocar la X)
  // tipo/tematica/color son multi-select, así que no cierran el drawer solos al tildar una opción
  const drawerFieldProps = {
    ...props,
    onToggleDestacado: () => { props.onToggleDestacado(); closeDrawer(); },
    onToggleNovedad: () => { props.onToggleNovedad(); closeDrawer(); },
    onClear: () => { props.onClear(); closeDrawer(); },
  };

  return (
    <>
      <div className={styles.sidebarWrapper} style={{ top: stickyTop }}>
        <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
          <span className={styles.titulo}>Filtros</span>
          <FiltrosFields {...props} activeCount={activeCount} />
        </aside>
        <button
          type="button"
          className={styles.collapseToggle}
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
        >
          <ChevronLeftIcon fontSize="small" style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>

      <button type="button" className={styles.filtrosButton} onClick={openDrawer}>
        <TuneIcon fontSize="small" />
        Filtros
        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
      </button>

      {drawerOpen &&
        <>
          <div className={`${styles.overlay} ${open ? styles.overlayOpen : ''}`} onClick={closeDrawer} />
          <div className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`} role="dialog" aria-modal="true" aria-label="Filtros">
            <div className={styles.drawerHeader}>
              <span className={styles.titulo}>Filtros</span>
              <IconButton ref={closeButtonRef} onClick={closeDrawer} aria-label="Cerrar filtros">
                <CloseIcon />
              </IconButton>
            </div>
            <div className={styles.drawerBody}>
              <FiltrosFields {...drawerFieldProps} activeCount={activeCount} />
            </div>
          </div>
        </>
      }
    </>
  );
};

export default FiltrosTienda;
